import React from "react";
import { Reveal } from "../components/Reveal";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { AlertTriangle } from "lucide-react";

/**
 * /terms — Terms of Service (Draft).
 * Wyoming LLC, English-first. Marked as draft until legal review.
 * Page is noindex'd to avoid Google indexing a draft legal page.
 */
export const Terms = () => {
    const updated = "February 11, 2026";

    return (
        <>
            <SEO
                title="Terms of Service"
                description="Terms of Service for Cybersecurity Forefront LLC — covering use of the chat-first firewall configurator, Client IPAM, compliance tools, and subscriptions."
                canonicalPath="/terms"
                noindex
            />
            <div
                className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 md:px-10"
                data-testid="terms-page"
            >
                <Reveal>
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Legal
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05]"
                        data-testid="terms-title"
                    >
                        Terms of Service
                    </h1>
                    <p className="mt-3 text-[12px] font-mono uppercase tracking-[0.2em] text-text-dim">
                        Last updated: {updated}
                    </p>
                </Reveal>

                {/* Draft banner */}
                <div
                    role="note"
                    className="mt-8 flex items-start gap-3 rounded-lg border border-accent-amber/40 bg-accent-amber/[0.06] p-4"
                    data-testid="terms-draft-banner"
                >
                    <AlertTriangle className="w-4 h-4 text-accent-amber mt-0.5 shrink-0" />
                    <p className="text-[13px] text-text-muted leading-relaxed">
                        <span className="text-accent-amber font-medium">
                            Draft — subject to legal review.
                        </span>{" "}
                        This document is provided in good faith and reflects our current
                        intent. It will be finalized by counsel before general availability.
                        Material changes will be communicated in-product and via email to
                        registered users.
                    </p>
                </div>

                <article className="mt-12 space-y-12 text-[14px] text-text-muted leading-[1.75]">
                    <section data-testid="terms-section-acceptance">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            1. Acceptance of Terms
                        </h2>
                        <p>
                            These Terms of Service (&ldquo;Terms&rdquo;) govern your access
                            to and use of the websites, applications, APIs, and services
                            (collectively, the &ldquo;Service&rdquo;) operated by{" "}
                            <strong className="text-text-primary">
                                Cybersecurity Forefront LLC
                            </strong>
                            , a Wyoming limited liability company with registered office at
                            30 N Gould St, Ste N, Sheridan, WY 82801, United States
                            (&ldquo;Cybersecurity Forefront,&rdquo; &ldquo;we,&rdquo;
                            &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an account
                            or using the Service, you agree to be bound by these Terms. If
                            you do not agree, do not use the Service.
                        </p>
                    </section>

                    <section data-testid="terms-section-eligibility">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            2. Eligibility & Account
                        </h2>
                        <p>
                            You must be at least 18 years old and able to enter into a
                            binding contract to use the Service. You are responsible for
                            maintaining the confidentiality of your credentials and for all
                            activity under your account. Notify us immediately at{" "}
                            <a
                                href="mailto:info@cybersecurityforefront.com"
                                className="text-accent-cyan hover:underline"
                            >
                                info@cybersecurityforefront.com
                            </a>{" "}
                            of any unauthorized use.
                        </p>
                    </section>

                    <section data-testid="terms-section-license">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            3. License Grant
                        </h2>
                        <p>
                            Subject to your compliance with these Terms and timely payment
                            of any applicable fees, Cybersecurity Forefront grants you a
                            limited, non-exclusive, non-transferable, revocable license to
                            access and use the Service for your internal business or
                            personal use. You may not resell, white-label, or sublicense the
                            Service except under a separately negotiated MSP / partner
                            agreement.
                        </p>
                    </section>

                    <section data-testid="terms-section-acceptable-use">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            4. Acceptable Use
                        </h2>
                        <p>You agree not to:</p>
                        <ul className="mt-3 list-disc pl-6 space-y-1.5">
                            <li>
                                use the Service to violate any law, regulation, or
                                third-party right;
                            </li>
                            <li>
                                attempt to reverse engineer, decompile, or extract source
                                code, except as expressly permitted by law;
                            </li>
                            <li>
                                use the Service to attack, scan, or compromise infrastructure
                                you do not own or are not explicitly authorized to test;
                            </li>
                            <li>
                                introduce malware, viruses, or other harmful code to the
                                Service;
                            </li>
                            <li>
                                circumvent rate limits, paywalls, or other technical access
                                controls.
                            </li>
                        </ul>
                    </section>

                    <section data-testid="terms-section-output">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            5. Generated Configurations & Output
                        </h2>
                        <p>
                            The Service generates firewall configurations, compliance
                            assessments, and other security artifacts based on inputs you
                            provide. <strong className="text-text-primary">
                                You are solely responsible
                            </strong>{" "}
                            for reviewing, validating, and deploying any output in your
                            environment. Cybersecurity Forefront does not warrant that
                            generated artifacts are free of errors or fit for any particular
                            production deployment. Always test in a lab before production
                            rollout.
                        </p>
                    </section>

                    <section data-testid="terms-section-subscriptions">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            6. Subscriptions, Billing & Refunds
                        </h2>
                        <p>
                            Paid plans renew automatically at the end of each billing period
                            unless canceled before renewal. Payments are processed by Stripe
                            and are non-refundable except where required by law. You may
                            cancel a subscription at any time from your account settings;
                            access continues until the end of the paid period.
                        </p>
                    </section>

                    <section data-testid="terms-section-ip">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            7. Intellectual Property
                        </h2>
                        <p>
                            Cybersecurity Forefront owns all rights, title, and interest in
                            the Service, including underlying software, design, content, and
                            trademarks. You own the inputs you submit and the resulting
                            outputs generated for your account, subject to the limited
                            license you grant us to operate, improve, and secure the
                            Service.
                        </p>
                    </section>

                    <section data-testid="terms-section-warranty">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            8. Disclaimer of Warranties
                        </h2>
                        <p>
                            THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
                            AVAILABLE.&rdquo; TO THE FULLEST EXTENT PERMITTED BY LAW, WE
                            DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
                            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                            NON-INFRINGEMENT.
                        </p>
                    </section>

                    <section data-testid="terms-section-liability">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            9. Limitation of Liability
                        </h2>
                        <p>
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, CYBERSECURITY FOREFRONT
                            SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                            CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA,
                            OR GOODWILL. OUR AGGREGATE LIABILITY ARISING OUT OF OR RELATED
                            TO THE SERVICE SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO US IN
                            THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE
                            CLAIM.
                        </p>
                    </section>

                    <section data-testid="terms-section-termination">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            10. Termination
                        </h2>
                        <p>
                            We may suspend or terminate your access at any time for breach
                            of these Terms or for risk to the Service or other users. Upon
                            termination, your right to use the Service ends immediately;
                            sections that by their nature survive termination shall survive.
                        </p>
                    </section>

                    <section data-testid="terms-section-law">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            11. Governing Law & Venue
                        </h2>
                        <p>
                            These Terms are governed by the laws of the State of Wyoming,
                            USA, without regard to its conflict-of-laws principles. Any
                            dispute shall be resolved exclusively in the state or federal
                            courts located in Sheridan County, Wyoming, and the parties
                            consent to personal jurisdiction therein.
                        </p>
                    </section>

                    <section data-testid="terms-section-changes">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            12. Changes to These Terms
                        </h2>
                        <p>
                            We may update these Terms from time to time. Material changes
                            will be communicated in-product or via email at least 14 days
                            before they take effect. Continued use of the Service after the
                            effective date constitutes acceptance.
                        </p>
                    </section>

                    <section data-testid="terms-section-contact">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            13. Contact
                        </h2>
                        <p>
                            Questions about these Terms? Email{" "}
                            <a
                                href="mailto:info@cybersecurityforefront.com"
                                className="text-accent-cyan hover:underline"
                            >
                                info@cybersecurityforefront.com
                            </a>{" "}
                            or write to Cybersecurity Forefront LLC, 30 N Gould St, Ste N,
                            Sheridan, WY 82801, USA.
                        </p>
                    </section>
                </article>

                <p className="mt-16 text-[11px] font-mono uppercase tracking-[0.2em] text-text-dim text-center">
                    {SITE_DOMAIN.replace("https://", "")} · Wyoming LLC · USA
                </p>
            </div>
        </>
    );
};

export default Terms;
